import { useEffect, useMemo, useState } from "react";
import Button from "@/components/Button/Button";
import DownloadIcon from "@/components/SVGComponents/DownloadIcon";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import ExportConfirmationModal from "@/components/ExportConfirmationModal/ExportConfirmationModal";
import { usePageSubtitle } from "@/hooks/usePageSubtitle";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getChapters } from "@/store/slices/chapterSlice";
import chapterService from "@/services/api/chapters";
import type { Chapter } from "@/services/api/chapters";
import ChapterCard from "./components/ChapterCard/ChapterCard";
import ChapterCardSkeleton from "./components/ChapterCard/ChapterCardSkeleton";
import VenuesSection from "./components/VenuesSection/VenuesSection";
import OpenChapterModal from "./components/OpenChapterModal/OpenChapterModal";
import styles from "./CitiesChapters.module.scss";

const FETCH_PARAMS = { pageNo: 1, perPage: 100 };

const CitiesChapters = () => {
  const dispatch = useAppDispatch();
  const { chapters, isFetching } = useAppSelector((state) => state.chapter);
  const [isOpenChapterModalOpen, setIsOpenChapterModalOpen] = useState(false);
  const [editingChapter, setEditingChapter] = useState<Chapter | null>(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  useEffect(() => {
    dispatch(getChapters(FETCH_PARAMS));
  }, [dispatch]);

  const liveCount = useMemo(() => chapters.filter((chapter) => chapter.membersCount > 0).length, [chapters]);
  const formingCount = chapters.length - liveCount;

  usePageSubtitle(
    isFetching
      ? "Loading chapters…"
      : `${liveCount} chapter${liveCount === 1 ? "" : "s"} live · ${formingCount} in formation`,
  );

  return (
    <div className={styles.page}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Chapters</h2>
        <div className={styles.sectionActions}>
          <Button
            outlined
            startIcon={<DownloadIcon />}
            className={styles.actionButton}
            onClick={() => setIsExportModalOpen(true)}
          >
            CHAPTER REPORT
          </Button>
          <Button
            startIcon={<PlusIcon width={14} height={14} />}
            className={styles.actionButton}
            onClick={() => setIsOpenChapterModalOpen(true)}
          >
            OPEN A CHAPTER
          </Button>
        </div>
      </div>

      <div className={styles.grid}>
        {isFetching
          ? Array.from({ length: 4 }).map((_, idx) => <ChapterCardSkeleton key={idx} />)
          : chapters.map((chapter) => (
              <ChapterCard key={chapter.id} chapter={chapter} onManage={setEditingChapter} />
            ))}
      </div>

      <VenuesSection />

      <OpenChapterModal
        isOpen={isOpenChapterModalOpen || editingChapter !== null}
        chapter={editingChapter}
        onClose={() => {
          setIsOpenChapterModalOpen(false);
          setEditingChapter(null);
        }}
      />

      <ExportConfirmationModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        title="Chapter report"
        fallbackFileName="chapter-report"
        fetchExportFile={() => chapterService.exportChapters()}
      />
    </div>
  );
};

export default CitiesChapters;
